import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type { MemberRosterState } from "@/core/activity-points/types"

import { toast } from "@/components/ui/toast"
import {
  activityPointsApi,
  type ActivityPointsMonth,
  type SaveActivityPointsMonthInput,
} from "@/lib/activity-points-api"
import { activityPointsKeys, adminMembersKeys } from "@/lib/query-keys"

export const useActivityPointsMonthQuery = (
  year: number,
  month: number,
  state: MemberRosterState,
  enabled = true,
) =>
  useQuery({
    queryKey: activityPointsKeys.month(year, month, state),
    queryFn: () => activityPointsApi.getMonth({ year, month, state }),
    enabled,
  })

export const useSaveActivityPointsMonthMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      state: _state,
      ...input
    }: SaveActivityPointsMonthInput & { state: MemberRosterState }) => {
      const result = await activityPointsApi.saveMonth(input)
      if (result.year !== input.year || result.month !== input.month) {
        throw new Error(
          "Saved activity-points period did not match the request",
        )
      }
      return result
    },
    onSuccess: (_result, input) => {
      const selectedKey = activityPointsKeys.month(
        input.year,
        input.month,
        input.state,
      )
      const monthKey = [...activityPointsKeys.all, input.year, input.month]
      const saved = new Map(
        input.entries.map(({ memberId, points }) => [memberId, points]),
      )
      const refreshes: Promise<void>[] = []
      for (const [
        key,
        cached,
      ] of queryClient.getQueriesData<ActivityPointsMonth>({
        queryKey: monthKey,
      })) {
        if (
          !cached?.rows.some(
            (row) =>
              saved.has(row.memberId) && saved.get(row.memberId) !== row.points,
          )
        )
          continue
        queryClient.setQueryData<ActivityPointsMonth>(key, {
          ...cached,
          rows: cached.rows.map((row) => {
            const points = saved.get(row.memberId)
            return points === undefined || points === row.points
              ? row
              : { ...row, points }
          }),
        })
        if (key[3] !== input.state)
          refreshes.push(
            queryClient.invalidateQueries({ queryKey: key, exact: true }),
          )
      }
      refreshes.push(
        queryClient.invalidateQueries({ queryKey: selectedKey, exact: true }),
      )
      for (const memberId of saved.keys()) {
        refreshes.push(
          queryClient.invalidateQueries({
            queryKey: adminMembersKeys.detail(memberId),
            exact: true,
          }),
        )
      }
      void Promise.all(refreshes).catch((error: Error) =>
        toast.error(error.message),
      )
    },
    onError: (error: Error) => toast.error(error.message),
  })
}
